import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Archive, Loader2, Search, CheckCircle2, Clock } from 'lucide-react';
import { tasksAPI } from '../lib/api';

// Histórico de tareas completadas hace más de 7 días (archivadas, nunca borradas). Filtros: cliente, fechas, texto.
export function ArchivedTasksDialog({ open, onClose, onOpenTask, archiveDays = 7 }) {
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [q, setQ] = useState('');
  const [client, setClient] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const d = await tasksAPI.getArchived({ ...(from ? { date_from: from } : {}), ...(to ? { date_to: to } : {}), ...(q ? { q } : {}) });
      setTasks(d.tasks || []);
    } catch (e) {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open) load(); }, [open, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const clients = useMemo(() => {
    const m = new Map();
    tasks.forEach((t) => { if (t.client_group_id) m.set(t.client_group_id, t.client_group_name || 'Cliente'); });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1], 'es'));
  }, [tasks]);
  const shown = useMemo(() => tasks.filter((t) => (!client || t.client_group_id === client) && (!q || (t.title || '').toLowerCase().includes(q.toLowerCase()))), [tasks, client, q]);
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
  const minutes = (t) => (t.time_spent_minutes || t.total_minutes || t.time_spent || 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="archived-tasks-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Archive className="w-5 h-5 text-primary" /> Tareas archivadas</DialogTitle>
          <DialogDescription>
            Completadas hace más de {archiveDays} días. Se conservan íntegras (incluido su tiempo registrado); solo dejan de verse en las listas activas.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Buscar por título…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" data-testid="archived-search" />
          </div>
          <Select value={client || '__all__'} onValueChange={(v) => setClient(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-9 w-[200px]" data-testid="archived-client-filter"><SelectValue placeholder="Cliente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los clientes</SelectItem>
              {clients.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[150px]" data-testid="archived-from" />
          <span className="text-muted-foreground text-sm">→</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-[150px]" data-testid="archived-to" />
          {(q || client || from || to) && (
            <Button variant="ghost" size="sm" onClick={() => { setQ(''); setClient(''); setFrom(''); setTo(''); }} data-testid="archived-clear">Limpiar</Button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto border rounded-md" data-testid="archived-list">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando histórico…</div>
          ) : shown.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-12">No hay tareas archivadas con esos filtros.</p>
          ) : (
            <div className="divide-y">
              {shown.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onOpenTask?.(t)}
                  className="w-full text-left flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40 transition-colors"
                  data-testid={`archived-task-${t.id}`}
                >
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{t.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {t.client_group_name || 'Sin cliente'}{t.assigned_to_name ? ` · ${t.assigned_to_name}` : ''}
                    </p>
                  </div>
                  {minutes(t) > 0 && (
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1 shrink-0"><Clock className="w-3 h-3" /> {Math.round(minutes(t) / 60 * 10) / 10} h</span>
                  )}
                  <span className="text-xs text-muted-foreground shrink-0 w-28 text-right">Completada {fmtDate(t.completed_at)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">{shown.length} de {tasks.length} tareas archivadas</p>
      </DialogContent>
    </Dialog>
  );
}
